import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [empresaActual, setEmpresaActual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.getMe();
      setUser(response.data);
      setIsAuthenticated(true);
      
      // Set empresa actual
      if (response.data.empresas?.length > 0) {
        const savedEmpresaId = localStorage.getItem('empresaActual');
        const empresa = savedEmpresaId 
          ? response.data.empresas.find(e => e.id === savedEmpresaId) || response.data.empresas[0]
          : response.data.empresas[0];
        setEmpresaActual(empresa);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    await loadUser();
    return response.data;
  };

  const register = async (nombre, email, password) => {
    const response = await authApi.register({ nombre, email, password });
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    await loadUser();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('empresaActual');
    setUser(null);
    setEmpresaActual(null);
    setIsAuthenticated(false);
  };

  const cambiarEmpresa = (empresa) => {
    setEmpresaActual(empresa);
    localStorage.setItem('empresaActual', empresa.id);
  };

  const hasPermission = (requiredRoles) => {
    if (!empresaActual) return false;
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return requiredRoles.includes(empresaActual.rol);
  };

  const isOwner = () => empresaActual?.rol === 'OWNER';
  const isManager = () => empresaActual?.rol === 'MANAGER';
  const isViewer = () => empresaActual?.rol === 'VIEWER';
  const canEdit = () => hasPermission(['OWNER', 'MANAGER']);

  const value = {
    user,
    empresaActual,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    cambiarEmpresa,
    hasPermission,
    isOwner,
    isManager,
    isViewer,
    canEdit,
    loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
